import React, { ReactElement, useCallback, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { useHistory } from 'react-router'
import { selectCreditCardStatisticsBudgetCount } from 'src/app/Account/redux/account.selectors'
import BackgroundPageContent from 'src/components/BackgroundPageContent/BackgroundPageContent'
import TableEmptyState, {
  TableEmptyStateDesigns,
} from 'src/components/TableEmptyState/TableEmptyState'
import CardBudgetsTable from 'src/feature/CardBudgetsTable/CardBudgetsTable'
import { useWindowSize } from 'src/hooks'
import { useIsUserOrglessRCNUser } from 'src/hooks/useOrglessUserStatus'
import { CreditCardType } from 'src/models/creditCard.models'
import { budgetOverviewUrl, createBudgetUrl } from 'src/navigation/urls'
import { useQueryCreditCards } from 'src/queries/creditCard.queries'
import { useQueryBudgetVirtualCardCounts } from 'src/queries/statistics/budgetVirtualCardCounts.queries'
import { useQueryStatistics } from 'src/queries/statistics/statistics.queries'
import { StatisticsKeys } from 'src/queries/statistics/statistics.queries.constants'
import { selectCalculatedBackgroundLocation } from 'src/redux/selectors/router.selectors'

/** Matches the legacy tab's page size, so paging behaviour is unchanged. */
const BUDGETS_PER_PAGE = 10

/**
 * The grid fills the viewport below the page chrome. Same constant the legacy
 * `AccountBudgets` used for the identical purpose.
 */
const PAGE_CHROME_HEIGHT = 122

interface AccountBudgetsTabProps {
  /** The card whose budgets to list. */
  creditCardId: string
}

/**
 * Redesigned Budgets tab content for the card-detail page (EX-40737).
 *
 * Replaces the legacy `AccountBudgets` (`src/app/`) when
 * `isUpdatedBudgetsTableEnabled` is on. Two things change together, and both are
 * why this is a new page rather than an edit to that one:
 *
 *  - the TABLE is `CardBudgetsTable`, the same component the Overview tab's
 *    snapshot renders, so a budget reports the same columns either side of the
 *    "view all budgets" link; and
 *  - the DATA comes from `useQueryCreditCards` — the same infinite query the
 *    redesigned Overview already runs — instead of the Redux `getCreditCards`
 *    thunk and its module-level paginated slice. That slice is shared mutable
 *    state keyed by nothing in particular, and reading it was what tied the
 *    legacy tab to a `useEffect`-on-mount fetch.
 *
 * The empty state is legacy's `TableEmptyState` again (EX-40737 follow-up). The
 * first cut replaced it with a plain sentence, which lost the illustration and —
 * more to the point — the Create Budget CTA that is the only thing to do on an
 * empty tab. `readOnly` branches on the same `canCreateBudget` gate the heading
 * CTA uses, so the two cannot offer a user a flow the other withholds.
 *
 * Routing stays in `AccountEntityPage`; this page owns its data and builds the
 * budget-detail destination, and the table itself stays routing-free.
 */
export default function AccountBudgetsTab(
  props: AccountBudgetsTabProps,
): ReactElement {
  const { creditCardId } = props
  const history = useHistory()
  // Resize-aware, like the legacy tab: the grid is sized in pixels, so a window
  // resize has to re-measure or the table keeps its old height.
  const [, windowHeight] = useWindowSize()
  // What should stay painted behind the create-budget flyout. Read here because
  // the flyout's own route declares `homeUrl()` as its default background, so a
  // push that does not carry this sends the user's page out from under them.
  const backgroundLocation = useSelector(selectCalculatedBackgroundLocation)
  /*
    The card's real budget total, not the number of rows loaded so far.

    Populated as a side effect of the credit-card detail query the parent page
    already runs, so reading it costs no request. Paging over the loaded count
    instead would rewrite the heading as the user scrolls — "10 budgets", then
    "20", then "30" for one unchanged card.

    `undefined` until the statistics land, which the table renders as no count
    rather than a zero.
  */
  const totalBudgetCount = useSelector(selectCreditCardStatisticsBudgetCount)

  // No `status` filter, matching legacy: the Budgets tab lists every budget on
  // the card, including inactive ones. That is the one filter difference from
  // the Overview snapshot, which shows only active budgets.
  const {
    data,
    isPending,
    isError,
    isFetching,
    hasNextPage,
    fetchNextPage,
    complete,
  } = useQueryCreditCards({
    filters: {
      type: CreditCardType.Delegate,
      count: BUDGETS_PER_PAGE,
      page: 0,
      sortField: 'activeUpdatedAt',
      parentCreditCard: creditCardId,
    },
  })

  const budgets = useMemo(() => data?.creditCards ?? [], [data?.creditCards])
  const budgetIds = useMemo(() => budgets.map((budget) => budget.id), [budgets])
  const cardCounts = useQueryBudgetVirtualCardCounts(budgetIds)

  // `fetch` is called by the grid when it reaches the end of the loaded rows.
  // The page argument is ignored: the infinite query owns its own cursor, and
  // taking one from the caller is how the two get out of step.
  const fetchMore = useCallback((): void => {
    if (!hasNextPage || isFetching) return
    void fetchNextPage()
  }, [hasNextPage, isFetching, fetchNextPage])

  /*
    The New budget CTA's gate, taken from `CreateNewDropdown` rather than
    invented here — the same two counts that decide whether "Create a budget"
    appears in the global Create New menu. A CTA on this table that the global
    menu would have withheld sends the user into a flow their account cannot
    complete.

    Orgless RCN users are excluded rather than routed: the dropdown sends them
    through a set-up-your-organization popup first, and reproducing that flow is
    out of scope here. They keep the Create New menu, which handles them
    properly. Dropping the shortcut is the honest failure mode; linking them
    straight to /budgets/create is not.
  */
  const { results: budgetCreationStatistics } = useQueryStatistics({
    statisticsKeys: [
      StatisticsKeys.CreditCardsSourceBudgetsEnabledCanIssueBudgetsCount,
      StatisticsKeys.CreditCardsActiveSourceBudgetsEnabledCanIssueBudgetsCount,
    ] as const,
  })
  const isUserOrglessRCNUser = useIsUserOrglessRCNUser()
  const canCreateBudget =
    !isUserOrglessRCNUser &&
    (budgetCreationStatistics[
      StatisticsKeys.CreditCardsSourceBudgetsEnabledCanIssueBudgetsCount
    ].data ?? 0) > 0 &&
    (budgetCreationStatistics[
      StatisticsKeys.CreditCardsActiveSourceBudgetsEnabledCanIssueBudgetsCount
    ].data ?? 0) > 0

  /*
    Carries `backgroundLocation` explicitly. `createBudgetRouteProps` declares a
    `calculateDefaultBackgroundLocation` of `homeUrl()` (see
    `modalBackgrounds.ts`), so pushing a bare path opens the flyout over HOME —
    the Budgets tab visibly disappears behind it, and dismissing the flyout
    leaves the user on the dashboard instead of the card they started from.

    The empty `search`/`hash` are part of the same contract the transaction
    flyout uses: a bare string push lets the new route inherit the current
    location's state.
  */
  const handleCreateBudget = useCallback((): void => {
    history.push({
      pathname: createBudgetUrl(),
      search: '',
      hash: '',
      state: { backgroundLocation },
    })
  }, [history, backgroundLocation])

  const handleSelectBudget = useCallback(
    (budgetId: string): void => {
      history.push(budgetOverviewUrl(budgetId))
    },
    [history],
  )

  return (
    // Every sibling tab supplies its own BackgroundPageContent — that is where
    // the page gutters live.
    <BackgroundPageContent>
      <div data-testid="accountBudgetsTab-content">
        <CardBudgetsTable
          budgets={budgets}
          cardCounts={cardCounts}
          isPending={isPending}
          isError={isError}
          totalCount={totalBudgetCount}
          // Rows are real links; the page owns the URL, the table owns the
          // click.
          budgetHref={budgetOverviewUrl}
          onSelectBudget={handleSelectBudget}
          onCreateBudget={canCreateBudget ? handleCreateBudget : undefined}
          emptyState={
            <TableEmptyState
              design={TableEmptyStateDesigns.AccountBudgets}
              readOnly={!canCreateBudget}
            />
          }
          infiniteScrollProps={{
            fetch: fetchMore,
            loading: isFetching,
            complete,
            hasNextPage,
          }}
          height={Math.max(windowHeight - PAGE_CHROME_HEIGHT, 0)}
        />
      </div>
    </BackgroundPageContent>
  )
}
